import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styles from "./Index.module.css";

// Import API service
import { getStudy, submitSurvey, saveSurveyDraft } from "../../../Config/api";
import DynamicFormRenderer from "./DynamicForm";
import ConsentSection from "./ConsentSection";

const MainFroms = ({ studyId: propStudyId, userId: propUserId }) => {

    const { studyId: routeStudyId } = useParams();
    const navigate = useNavigate();

    const getUserId = () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const userId = user.userId || user.user_id || user.id;
            return userId || null;
        } catch (error) {
            console.error('Error:', error);
            return null;
        }
    };
    const getUserName = () => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            return user.fullName ||
                'Doctor';
        } catch (error) {
            console.error('Error getting user name:', error);
            return 'Doctor';
        }
    };

    const studyId = propStudyId || routeStudyId;
    const userId = getUserId();

    const [studyData, setStudyData] = useState(null);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [formData, setFormData] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    const [hasConsented, setHasConsented] = useState(false);
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    const [lastSaveTime, setLastSaveTime] = useState(null);

    // Toast notification function
    const showToast = (title, description, variant = 'default') => {
        setToast({ title, description, variant });
        setTimeout(() => setToast(null), 5000);
    };

    // Auto-save draft function
    const autoSaveDraft = useCallback(async (data, sectionIndex, consented) => {
        if (isSubmitted) {
            console.log(' Skipping auto-save: Survey already submitted');
            return;
        }

        try {
            console.log(' Auto-saving draft...');
            setIsSavingDraft(true);

            const draftData = {
                ...data,
                currentSectionIndex: sectionIndex,
                hasConsented: consented,
                lastSaved: new Date().toISOString()
            };

            await saveSurveyDraft(userId, studyId, draftData);
            setLastSaveTime(new Date());
            console.log(' Draft auto-saved successfully');

        } catch (error) {
            console.error(' Auto-save failed:', error);
        } finally {
            setIsSavingDraft(false);
        }
    }, [userId, studyId, isSubmitted]);

    // Auto-save effect
    useEffect(() => {
        if (isSubmitted) {
            console.log(' Skipping auto-save: Survey already submitted');
            return;
        }

        if (!isLoading && studyData && Object.keys(formData).length > 0) {
            const timeoutId = setTimeout(() => {
                autoSaveDraft(formData, currentSectionIndex, hasConsented);
            }, 2000);

            return () => clearTimeout(timeoutId);
        }
    }, [formData, currentSectionIndex, hasConsented, autoSaveDraft, isLoading, studyData, isSubmitted]);

    // Fetch study data on component mount
    useEffect(() => {
        const fetchStudyData = async () => {
            if (!userId || !studyId) {
                setIsLoading(false);
                setError('Missing required parameters');
                return;
            }

            try {
                setIsLoading(true);
                setError(null);

                console.log(` Fetching study: userId=${userId}, studyId=${studyId}`);
                const response = await getStudy(userId, studyId);

                const submissionStatus = response?.data?.status;

                if (submissionStatus === 'submitted') {
                    console.log('  Already submitted - showing Thank You page');
                    setIsSubmitted(true);
                    setIsLoading(false);
                    return;
                }

                let studyDef = response?.data?.study_definition;

                if (Array.isArray(studyDef) && studyDef.length > 0) {
                    studyDef = studyDef[0];
                }

                if (studyDef && typeof studyDef === 'object' && studyDef.study_definition) {
                    studyDef = studyDef.study_definition;
                }

                if (typeof studyDef === 'string') {
                    studyDef = JSON.parse(studyDef);
                }

                const study = studyDef?.study || studyDef;

                if (!study || !study.sections || !Array.isArray(study.sections)) {
                    throw new Error('Invalid study structure: sections not found');
                }

                const filteredStudy = {
                    ...study,
                    sections: study.sections.filter(section => section.id !== 'consent')
                };

                console.log('  Study loaded with', filteredStudy.sections.length, 'sections');
                setStudyData(filteredStudy);

                // Load draft response
                const draftResp = response?.data?.draft_response;
                if (draftResp) {
                    const draft = typeof draftResp === 'string' ? JSON.parse(draftResp) : draftResp;
                    console.log(' Draft response found:', draft);

                    const { currentSectionIndex: savedSection, hasConsented: savedConsent, lastSaved, ...savedFormData } = draft;

                    setFormData(savedFormData);

                    if (typeof savedSection === 'number' && savedSection >= 0) {
                        setCurrentSectionIndex(savedSection);
                        console.log('📍 Resuming from section:', savedSection);
                    }

                    if (savedConsent) {
                        setHasConsented(true);
                        console.log('  Consent already given');
                    }

                    if (lastSaved) {
                        showToast('Draft Loaded', 'Your previous progress has been restored', 'success');
                    }
                }

            } catch (err) {
                console.error('  Error:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchStudyData();
    }, [userId, studyId]);

    useEffect(() => {
        if (!studyData || isSubmitted || isLoading) return;

        const isLastSection = currentSectionIndex === studyData.sections.length - 1;

        if (isLastSection) {
            const lastSection = studyData.sections[currentSectionIndex];

            // Find the doctor name field - adjust this based on your field ID
            const doctorField = lastSection.questions.find(q => {
                const id = (q.id || '').toLowerCase();
                const text = (q.text || '').toLowerCase();

                return id.includes('doctor') ||
                    id.includes('physician') ||
                    id.includes('investigator') ||
                    text.includes('doctor name') ||
                    text.includes('physician name') ||
                    text.includes('investigator name');
            });

            if (doctorField) {
                // Check if field is already filled
                const isAlreadyFilled = formData[doctorField.id] &&
                    formData[doctorField.id].trim() !== '';

                if (!isAlreadyFilled) {
                    const userName = getUserName();
                    console.log('🩺 Auto-populating doctor name:', userName);

                    updateFormData({
                        [doctorField.id]: userName
                    });

                    // Optional: Show a toast notification
                    showToast(
                        'Field Auto-Filled',
                        'Doctor name has been filled with your account name',
                        'success'
                    );
                }
            }
        }
    }, [currentSectionIndex, studyData, isSubmitted, isLoading]);

    // Update form data
    const updateFormData = (data) => {
        setFormData(prev => ({ ...prev, ...data }));
    };

    // Navigate to specific section (for progress bar clicks) - WITH VALIDATION
    const navigateToSection = async (sectionIndex) => {
        // Prevent skipping ahead - can only go to completed sections or next one
        if (sectionIndex > currentSectionIndex + 1) {
            showToast('Cannot Skip Sections', 'Please complete the current section first before jumping ahead.', 'error');
            return;
        }

        if (sectionIndex !== currentSectionIndex && sectionIndex >= 0 && sectionIndex < studyData.sections.length) {
            setCurrentSectionIndex(sectionIndex);
            await autoSaveDraft(formData, sectionIndex, hasConsented);
            window.scrollTo(0, 0);
        }
    };

    //  Validate all sections are complete
    // const validateAllSections = () => {
    //     const incompleteSections = [];

    //     studyData.sections.forEach((section, index) => {
    //         section.questions.forEach(question => {
    //             if (question.required) {
    //                 const value = formData[question.id];
    //                 if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
    //                     if (!incompleteSections.includes(index)) {
    //                         incompleteSections.push(index);
    //                     }
    //                 }
    //             }
    //         });
    //     });

    //     return incompleteSections;
    // };
    //  Validate all sections are complete
    const validateAllSections = () => {
        const incompleteSections = [];

        // Helper function to check if question should be disabled (matches DynamicFormRenderer logic)
        const isQuestionDisabled = (question) => {
            if (!question.dependsOn) return false;

            const { questionId, condition, values } = question.dependsOn;
            const dependentValue = formData[questionId];

            // For e11 and e14: Enable only when e10/e13 is "Yes" or "Sometimes"
            if (condition === 'enableWhenIn') {
                return !values.includes(dependentValue);
            }

            // Add other condition types if needed
            return false;
        };

        studyData.sections.forEach((section, index) => {
            section.questions.forEach(question => {
                // Skip validation if question is disabled
                if (isQuestionDisabled(question)) {
                    console.log(`Skipping validation for ${question.id} - question is disabled`);
                    return;
                }

                if (question.required) {
                    const value = formData[question.id];
                    if (!value || (Array.isArray(value) && value.length === 0) || value === '') {
                        console.log(`Validation failed for ${question.id} - value is empty`);
                        if (!incompleteSections.includes(index)) {
                            incompleteSections.push(index);
                        }
                    }
                }
            });
        });

        console.log('Incomplete sections:', incompleteSections);
        return incompleteSections;
    };
    // Navigate to next section
    const handleNext = async () => {
        console.log('Current section:', currentSectionIndex, 'of', studyData.sections.length - 1);

        if (currentSectionIndex < studyData.sections.length - 1) {
            const nextIndex = currentSectionIndex + 1;
            setCurrentSectionIndex(nextIndex);
            await autoSaveDraft(formData, nextIndex, hasConsented);
            window.scrollTo(0, 0);
        } else {
            //  VALIDATE ALL SECTIONS BEFORE SUBMISSION
            const incompleteSections = validateAllSections();

            if (incompleteSections.length > 0) {
                const sectionNumbers = incompleteSections.map(i => i + 1).join(', ');
                showToast(
                    'Incomplete Sections',
                    `Please complete all required fields in section(s): ${sectionNumbers}`,
                    'error'
                );
                // Navigate to first incomplete section
                setCurrentSectionIndex(incompleteSections[0]);
                window.scrollTo(0, 0);
                return;
            }

            handleSubmit();
        }
    };

    // Navigate to previous section
    const handleBack = async () => {
        if (currentSectionIndex > 0) {
            const prevIndex = currentSectionIndex - 1;
            setCurrentSectionIndex(prevIndex);
            await autoSaveDraft(formData, prevIndex, hasConsented);
            window.scrollTo(0, 0);
        }
    };

    // Handle consent acceptance
    const handleConsentAccept = async () => {
        console.log(' Consent accepted');
        setHasConsented(true);
        const updatedData = { ...formData, consent_agreement: true };
        setFormData(updatedData);
        await autoSaveDraft(updatedData, 0, true);
    };

    // Handle survey submission
    const handleSubmit = async () => {
        setIsSubmitting(true);

        try {
            const submissionData = {
                ...formData,
                submittedAt: new Date().toISOString()
            };

            console.log('Submitting survey:', submissionData);
            const response = await submitSurvey(userId, studyId, submissionData);

            if (response.success) {
                showToast('Success!', 'Your survey has been submitted successfully.', 'success');
                setIsSubmitted(true);
            } else {
                throw new Error(response.message || 'Submission failed');
            }

        } catch (error) {
            console.error('Submission error:', error);
            showToast('Error', error.message || 'Failed to submit survey. Please try again.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Invalid survey link
    if (!studyId) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorContainer}>
                    <div className={styles.errorCard}>
                        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <h2 className={styles.errorTitle}>Invalid Survey Link</h2>
                        <p className={styles.errorText}>The survey link is missing required parameters.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Loading state
    if (isLoading) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.loadingContainer}>
                    <div className={styles.loadingSpinner}>
                        <svg className={styles.spinner} viewBox="0 0 50 50">
                            <circle className={styles.spinnerPath} cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
                        </svg>
                    </div>
                    <p className={styles.loadingText}>Loading survey...</p>
                    <p className={styles.loadingSubtext}>Please wait while we prepare your survey</p>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorContainer}>
                    <div className={styles.errorCard}>
                        <svg className={styles.errorIcon} viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                        </svg>
                        <h2 className={styles.errorTitle}>Failed to Load Survey</h2>
                        <p className={styles.errorText}>{error}</p>
                        <button onClick={() => window.location.reload()} className={styles.retryButton}>
                            <svg className={styles.buttonIcon} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                            </svg>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Success/Submitted state
    if (isSubmitted) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.successContainer}>
                    <div className={styles.successCard}>
                        <div className={styles.successIconWrapper}>
                            <svg className={styles.successIcon} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <h2 className={styles.successTitle}>Thank You!</h2>
                        <p className={styles.successText}>
                            Your response has been successfully submitted. We appreciate your participation
                            in this important study.
                        </p>
                        <div className={styles.successDetails}>
                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Study Number:</span>
                                <span className={styles.detailValue}>NIS/2025/33</span>
                            </div>
                            <div className={styles.detailItem}>
                                <span className={styles.detailLabel}>Submitted:</span>
                                <span className={styles.detailValue}>{new Date().toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "short",
                                    year: "numeric",
                                    hour: "2-digit",
                                    minute: "2-digit",
                                    second: "2-digit",
                                    hour12: false,
                                })}</span>
                            </div>
                        </div>
                        <button onClick={() => navigate('/')} className={styles.homeButton}>
                            Return to Dashboard
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Show consent form if not consented yet
    if (!hasConsented) {
        return (
            <div className={styles.pageContainer}>
                {isSavingDraft && (
                    <div className={styles.savingIndicator}>
                        <svg className={styles.savingSpinner} viewBox="0 0 24 24">
                            <circle className={styles.savingCircle} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        </svg>
                        <span>Saving...</span>
                    </div>
                )}
                <ConsentSection onNext={handleConsentAccept} />
            </div>
        );
    }

    // Get current section
    const currentSection = studyData?.sections[currentSectionIndex];
    const isLastSection = currentSectionIndex === studyData?.sections.length - 1;

    return (
        <div className={styles.pageContainer}>
            {/* Auto-save indicator */}
            {isSavingDraft && (
                <div className={styles.savingIndicator}>
                    <svg className={styles.savingSpinner} viewBox="0 0 24 24">
                        <circle className={styles.savingCircle} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    </svg>
                    <span>Saving draft...</span>
                </div>
            )}

            {/* Toast Notification */}
            {toast && (
                <div className={`${styles.toast} ${styles[`toast${toast.variant.charAt(0).toUpperCase() + toast.variant.slice(1)}`]}`}>
                    <div className={styles.toastIcon}>
                        {toast.variant === 'success' && (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                        )}
                        {toast.variant === 'error' && (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        )}
                        {toast.variant === 'default' && (
                            <svg viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                        )}
                    </div>
                    <div className={styles.toastContent}>
                        <strong className={styles.toastTitle}>{toast.title}</strong>
                        <p className={styles.toastDescription}>{toast.description}</p>
                    </div>
                    <button onClick={() => setToast(null)} className={styles.toastClose}>
                        <svg viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}

            {/* Header */}
            <header className={styles.header}>
                <div className={styles.headerContent}>
                    <div className={styles.headerTop}>
                        <h1 className={styles.mainTitle}>{studyData?.title || 'Clinical Study Survey'}</h1>
                        <div className={styles.headerBadge}>
                            <svg className={styles.badgeIcon} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                            </svg>
                            <span>NIS/2025/33</span>
                        </div>
                    </div>
                    <p className={styles.headerSubtitle}>
                        Version {studyData?.version || '1.0'} • {studyData?.sections?.length || 0} Sections
                    </p>
                </div>
            </header>

            {/* Progress Bar - Simple Timeline */}
            {studyData && studyData.sections.length > 1 && (
                <div className={styles.progressSection}>
                    <div className={styles.progressInfo}>
                        <span className={styles.progressText}>
                            Section {currentSectionIndex + 1} of {studyData.sections.length}
                        </span>
                        <span className={styles.progressPercentage}>
                            {Math.round(((currentSectionIndex + 1) / studyData.sections.length) * 100)}% Complete
                        </span>
                    </div>

                    <div className={styles.progressTimeline}>
                        {studyData.sections.map((section, index) => (
                            <React.Fragment key={section.id}>
                                <button
                                    onClick={() => navigateToSection(index)}
                                    className={`${styles.progressDot} ${index <= currentSectionIndex ? styles.progressDotActive : ''} ${index === currentSectionIndex ? styles.progressDotCurrent : ''}`}
                                    type="button"
                                    title={`Section ${index + 1}: ${section.title}`}
                                >
                                    {index < currentSectionIndex ? (
                                        <svg viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                        </svg>
                                    ) : (
                                        <span>{index + 1}</span>
                                    )}
                                </button>
                                {index < studyData.sections.length - 1 && (
                                    <div className={`${styles.progressLine} ${index < currentSectionIndex ? styles.progressLineActive : ''}`}></div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            )}

            {/* Main Content */}
            <main className={styles.mainContent}>
                {currentSection && (
                    <DynamicFormRenderer
                        section={currentSection}
                        data={formData}
                        onUpdate={updateFormData}
                        onNext={handleNext}
                        onBack={currentSectionIndex > 0 ? handleBack : null}
                        isLastSection={isLastSection}
                        isSubmitting={isSubmitting}
                        isSubmitted={isSubmitted}
                    />
                )}
            </main>
        </div>
    );
};

export default MainFroms;